import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Search, Calendar, User, Loader2, Printer, Share2, FileText,
  ArrowUpRight, ArrowDownLeft, Pencil, ShoppingCart, Receipt as ReceiptIcon,
  UserCog, ChevronLeft, RotateCcw, X, Package,
} from "lucide-react";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";
import { printHTML } from "../../utils/printHTML";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// Split ISO date into short day + time. Used for the side date badge so
// the admin can scan dates fast without reading a long timestamp.
const splitDate = (iso) => {
  if (!iso) return { day: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString("en-GB", { year: "2-digit", month: "2-digit", day: "2-digit" }),
      time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch { return { day: iso, time: "" }; }
};
const SUPPORT_PHONES = [
  { local: "07717637525", label: "خدمة العملاء" },
  { local: "07842220088", label: "الدعم الفني" },
];

const STATUS_LABEL = {
  pending: "قيد المعالجة",
  confirmed: "مؤكدة",
  mujahez_assigned: "بانتظار التجهيز",
  mujahez_preparing: "قيد التجهيز",
  ready_for_driver: "جاهز للسائق",
  assigned: "معيّن سائق",
  accepted: "قَبِلها السائق",
  on_the_way: "بالطريق",
  delivering: "قيد التوصيل",
  awaiting_payment_confirmation: "بانتظار تأكيد الدفع",
  delivered: "تم التوصيل",
  rejected: "مرفوضة",
};

// ─── Customer search & picker ──────────────────────────────────
// شبيه بسند القبض: ادخل بحث → قائمة زبائن → اختر واحد.
const CustomerPicker = ({ selected, onSelect, onClear }) => {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    // Load once on mount — we'll filter client-side with smart Arabic search.
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/customers`);
        setList(data || []);
      } catch {
        toast.error("فشل تحميل الزبائن");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return list.slice(0, 20);
    return list
      .filter((u) =>
        smartMatch(
          `${u.full_name || ""} ${u.market_name || ""} ${u.phone || ""} ${u.customer_id || ""}`,
          q
        )
      )
      .slice(0, 30);
  }, [list, q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (selected) {
    return (
      <div
        className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4 flex items-center gap-3"
        data-testid="cs-selected-customer"
      >
        <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center shrink-0">
          <User className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-lg truncate">{selected.full_name}</p>
          <p className="text-xs opacity-90 truncate">
            {selected.market_name && <>🏪 {selected.market_name} · </>}
            {selected.customer_id && <>ايباد #{selected.customer_id} · </>}
            <span dir="ltr">{selected.phone}</span>
          </p>
        </div>
        <button
          onClick={onClear}
          data-testid="cs-clear-customer"
          className="bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
          title="تغيير الزبون"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative" data-testid="cs-picker">
      <div className="bg-white border-2 border-[#F5A623] rounded-2xl p-3">
        <label className="text-xs font-bold text-[#1E3A5F] flex items-center gap-1 mb-1">
          <User className="w-3.5 h-3.5" /> اختر الزبون <span className="text-[#E85D45]">*</span>
        </label>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            data-testid="cs-picker-search"
            placeholder="اسم الزبون / الماركت / رقم الايباد / الهاتف"
            className="w-full pr-9 pl-4 py-2.5 rounded-xl border-2 border-[#F5A623]/60 bg-white focus:outline-none focus:border-[#F5A623] text-sm"
          />
        </div>
        {open && (
          <div className="absolute left-3 right-3 top-full mt-1 bg-white border-2 border-[#F5A623] rounded-2xl shadow-2xl max-h-80 overflow-y-auto z-20">
            {loading ? (
              <div className="p-4 text-center text-[#5C6E64]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-center text-[#5C6E64] text-sm">لا توجد نتائج</p>
            ) : (
              filtered.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { onSelect(u); setOpen(false); setQ(""); }}
                  data-testid={`cs-picker-row-${u.id}`}
                  className="w-full text-right p-2.5 hover:bg-[#FFF8EC] border-b border-[#E4EBE6] last:border-b-0 flex items-center gap-2"
                >
                  <div className="bg-[#F5A623]/20 rounded-full w-9 h-9 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-[#F5A623]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#111814] truncate">{u.full_name}</p>
                    <p className="text-[10px] text-[#5C6E64] truncate">
                      {u.market_name && <>{u.market_name} · </>}
                      {u.customer_id && <>ايباد #{u.customer_id} · </>}
                      <span dir="ltr">{u.phone}</span>
                    </p>
                  </div>
                  {Number(u.customer_debt || 0) > 0 && (
                    <span className="text-[10px] bg-[#FEE2E2] text-[#B91C1C] px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                      دين <span dir="ltr">{fmt(u.customer_debt)}</span>
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Event card ─────────────────────────────────────────────────
// كل حركة بلونها الخاص:
//   • طلب/بيع → أحمر (زيادة دين) — بيع مباشر بترتقالي، بيع مشرف ببنفسجي
//   • سند قبض → أخضر (نقصان دين)
//   • تعديل يدوي → أزرق (زيادة أو نقصان)
const EventCard = ({ ev, idx, isLast }) => {
  const isOrder = ev.type === "order";
  const isReceipt = ev.type === "receipt";
  const isManual = ev.type === "manual";
  const isPurchFromCust = ev.type === "purchase_from_customer";
  const delta = Number(ev.delta || 0);
  const balAfter = Number(ev.balance_after || 0);

  // Color scheme per source
  let icon = ShoppingCart;
  let border = "border-[#E4EBE6]";
  let bg = "bg-white";
  let sideColor = "bg-[#5C6E64]";
  let sideText = "text-white";
  let title = ev.description;
  let sourceLabel = null;
  let sourceBadgeClass = "";

  if (isOrder) {
    icon = ShoppingCart;
    if (ev.source === "بيع المشرف") {
      sideColor = "bg-[#7C3AED]"; // بنفسجي
      border = "border-[#A855F7]/40";
      bg = "bg-[#FAF5FF]";
      sourceLabel = ev.source_extra ? `المشرف: ${ev.source_extra}` : "بيع المشرف";
      sourceBadgeClass = "bg-[#EDE9FE] text-[#6D28D9]";
    } else if (ev.source === "بيع مباشر") {
      sideColor = "bg-[#F5A623]"; // برتقالي
      border = "border-[#F5A623]/50";
      bg = "bg-[#FFFBEB]";
      sourceLabel = "بيع مباشر";
      sourceBadgeClass = "bg-[#FFF8EC] text-[#a06e10]";
    } else {
      sideColor = "bg-[#3B82F6]"; // أزرق
      border = "border-[#3B82F6]/40";
      bg = "bg-[#EFF6FF]";
      sourceLabel = "طلب من التطبيق";
      sourceBadgeClass = "bg-[#DBEAFE] text-[#1E40AF]";
    }
    title = `فاتورة ${ev.ref}`;
  } else if (isReceipt) {
    icon = ReceiptIcon;
    sideColor = "bg-[#15803D]"; // أخضر
    border = "border-[#22C55E]/40";
    bg = "bg-[#F0FDF4]";
    title = `سند قبض ${ev.ref}`;
    sourceLabel = "قبض نقدي";
    sourceBadgeClass = "bg-[#DCFCE7] text-[#15803D]";
  } else if (isPurchFromCust) {
    // شراء من الزبون — accounting inversion: increases their credit
    icon = ShoppingCart;
    sideColor = "bg-[#0EA5E9]"; // سماوي
    border = "border-[#0EA5E9]/40";
    bg = "bg-[#F0F9FF]";
    title = `شراء منّه — قائمة ${ev.ref}`;
    sourceLabel = "🔄 شراء من الزبون";
    sourceBadgeClass = "bg-[#CFFAFE] text-[#0369A1]";
  } else if (isManual) {
    icon = UserCog;
    sideColor = "bg-[#1E3A5F]"; // أزرق داكن
    border = "border-[#1E3A5F]/40";
    bg = "bg-[#F0F9FF]";
    title = ev.description;
    sourceLabel = "تعديل يدوي";
    sourceBadgeClass = "bg-[#DBEAFE] text-[#1E3A5F]";
  }

  const Icon = icon;
  const dt = splitDate(ev.date);

  return (
    <div className="flex gap-2 items-stretch" data-testid={`cs-event-${idx}`}>
      {/* Timeline column */}
      <div className="relative flex flex-col items-center shrink-0" style={{ width: 36 }}>
        <div className={`${sideColor} ${sideText} rounded-full w-9 h-9 flex items-center justify-center shadow z-10`}>
          <Icon className="w-4 h-4" />
        </div>
        {!isLast && <div className="flex-1 w-0.5 bg-[#E4EBE6] my-1" />}
      </div>

      {/* Card */}
      <div className={`flex-1 rounded-2xl border-2 ${border} ${bg} p-3 mb-2`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          {/* ─── Prominent date badge (right side in RTL, first child) ─── */}
          <div
            data-testid={`cs-event-date-${idx}`}
            className={`shrink-0 rounded-xl px-2 py-1.5 text-center border-2 ${
              isOrder && ev.source === "بيع المشرف" ? "border-[#A855F7] bg-[#EDE9FE] text-[#6D28D9]" :
              isOrder && ev.source === "بيع مباشر" ? "border-[#F5A623] bg-[#FFF8EC] text-[#a06e10]" :
              isOrder ? "border-[#3B82F6] bg-[#DBEAFE] text-[#1E40AF]" :
              isReceipt ? "border-[#22C55E] bg-[#DCFCE7] text-[#15803D]" :
              isPurchFromCust ? "border-[#0EA5E9] bg-[#CFFAFE] text-[#0369A1]" :
              "border-[#1E3A5F] bg-[#DBEAFE] text-[#1E3A5F]"
            }`}
            title={fmtDate(ev.date)}
          >
            <p className="text-[10px] font-black leading-none" dir="ltr">{dt.day}</p>
            <p className="text-[9px] opacity-80 mt-0.5" dir="ltr">{dt.time}</p>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
              <span className="font-bold text-sm text-[#111814]">{title}</span>
              {sourceLabel && (
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${sourceBadgeClass}`}>
                  {sourceLabel}
                </span>
              )}
              {isOrder && (ev.confirmed
                ? <span className="text-[9px] bg-[#DCFCE7] text-[#15803D] px-1.5 py-0.5 rounded-full font-bold">✓ مؤكدة</span>
                : <span className="text-[9px] bg-[#FEF3C7] text-[#B45309] px-1.5 py-0.5 rounded-full font-bold">⏳ غير مؤكدة</span>
              )}
              {isOrder && ev.edited && (
                <span className="text-[9px] bg-[#FEE2E2] text-[#B91C1C] px-1.5 py-0.5 rounded-full font-bold" title={ev.edited_by ? `عدّلها: ${ev.edited_by}` : ""}>
                  <Pencil className="w-2.5 h-2.5 inline" /> عُدّلت
                </span>
              )}
              {isOrder && ev.status && ev.status !== "confirmed" && (
                <span className="text-[9px] bg-[#F3F6F4] text-[#5C6E64] px-1.5 py-0.5 rounded-full">
                  {STATUS_LABEL[ev.status] || ev.status}
                </span>
              )}
            </div>
            {isOrder && (
              <div className="flex flex-wrap gap-1 mt-1 text-[10px] text-[#5C6E64]">
                <span className="bg-white border border-[#E4EBE6] rounded-full px-2 py-0.5">
                  <Package className="w-2.5 h-2.5 inline" /> {ev.items_count || "—"} صنف
                </span>
                <span className="bg-white border border-[#E4EBE6] rounded-full px-2 py-0.5">
                  إجمالي الفاتورة: <b dir="ltr">{fmt(ev.invoice_total)}</b>
                </span>
                {ev.paid_at_creation > 0 && (
                  <span className="bg-[#DCFCE7] text-[#15803D] rounded-full px-2 py-0.5 font-bold">
                    مدفوع نقداً: <span dir="ltr">{fmt(ev.paid_at_creation)}</span>
                  </span>
                )}
              </div>
            )}
            {isReceipt && (
              <div className="flex flex-wrap gap-1 mt-1 text-[10px] text-[#5C6E64]">
                {ev.cash_amount > 0 && (
                  <span className="bg-white border border-[#E4EBE6] rounded-full px-2 py-0.5">
                    نقداً: <b dir="ltr">{fmt(ev.cash_amount)}</b>
                  </span>
                )}
                {ev.discount > 0 && (
                  <span className="bg-[#FEF3C7] text-[#B45309] rounded-full px-2 py-0.5 font-bold">
                    خصم: <span dir="ltr">{fmt(ev.discount)}</span>
                  </span>
                )}
                {ev.notes && <span className="italic">📝 {ev.notes}</span>}
              </div>
            )}
            {isPurchFromCust && (
              <div className="flex flex-wrap gap-1 mt-1 text-[10px] text-[#5C6E64]">
                <span className="bg-white border border-[#E4EBE6] rounded-full px-2 py-0.5">
                  <Package className="w-2.5 h-2.5 inline" /> {ev.items_count || "—"} صنف
                </span>
                <span className="bg-white border border-[#E4EBE6] rounded-full px-2 py-0.5">
                  إجمالي القائمة: <b dir="ltr">{fmt(ev.invoice_total)}</b>
                </span>
                {ev.paid_at_creation > 0 && (
                  <span className="bg-[#FEE2E2] text-[#B91C1C] rounded-full px-2 py-0.5 font-bold">
                    دفعنا له نقداً: <span dir="ltr">{fmt(ev.paid_at_creation)}</span>
                  </span>
                )}
                {ev.edited && (
                  <span className="bg-[#FEF3C7] text-[#B45309] rounded-full px-2 py-0.5 font-bold">
                    <Pencil className="w-2.5 h-2.5 inline" /> عُدّلت
                  </span>
                )}
              </div>
            )}
            {isManual && ev.actor_name && (
              <p className="text-[10px] text-[#5C6E64] mt-1">👤 بواسطة: {ev.actor_name}</p>
            )}
          </div>

          {/* Delta + running balance */}
          <div className="text-left shrink-0">
            <div className={`inline-flex items-center gap-0.5 rounded-lg px-2 py-1 font-black text-sm ${
              delta > 0 ? "bg-[#FEE2E2] text-[#B91C1C]" :
              delta < 0 ? "bg-[#DCFCE7] text-[#15803D]" :
              "bg-[#F3F6F4] text-[#5C6E64]"
            }`}>
              {delta > 0 ? <ArrowUpRight className="w-3 h-3" /> :
               delta < 0 ? <ArrowDownLeft className="w-3 h-3" /> : null}
              <span dir="ltr">{delta > 0 ? "+" : ""}{fmt(delta)}</span>
            </div>
            <p className="text-[9px] text-[#5C6E64] mt-1">الرصيد بعدها</p>
            <p className={`text-xs font-bold ${
              balAfter > 0 ? "text-[#B91C1C]" : balAfter < 0 ? "text-[#1E3A5F]" : "text-[#15803D]"
            }`} dir="ltr">
              {fmt(Math.abs(balAfter))} د.ع
              {balAfter < 0 && <span className="text-[9px]"> (له)</span>}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Balance Edit Modal ────────────────────────────────────────
// مربّع تعديل رصيد الزبون. صندوقين واضحين:
//   • على الزبون (يطلبنا) — رقم موجب. مثال 900 = هذا الرقم دينه علينا.
//   • علينا (نطلبه)      — رقم موجب لكن يُخزَّن بالسالب.
// فقط واحد من الصندوقين يكون فيه قيمة > 0. الحفظ يستدعي
// `PATCH /admin/customer-debt/{id}` مع `{set: value}` والقيمة تنتشر
// لجميع الجهات (الأرصدة، كشوف الحسابات، تقارير المبيعات).
const BalanceEditModal = ({ customer, currentDebt, onClose, onSaved }) => {
  const [mode, setMode] = useState(currentDebt >= 0 ? "customer_owes" : "we_owe");
  const [value, setValue] = useState(String(Math.abs(currentDebt) || 0));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const num = parseFloat(value) || 0;
    const signed = mode === "we_owe" ? -Math.abs(num) : Math.abs(num);
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/customer-debt/${customer.id}`, {
        set: signed,
        reason: reason.trim() || "تعديل يدوي من كشف الحساب",
      });
      toast.success("تم تعديل رصيد الزبون بنجاح — ينعكس على كل الجهات");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التعديل");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cs-edit-balance-modal">
        <div className="flex items-center justify-between border-b border-[#E4EBE6] pb-2">
          <div className="flex items-center gap-2">
            <div className="bg-[#F5A623] text-white w-9 h-9 rounded-full flex items-center justify-center">
              <Pencil className="w-4 h-4" />
            </div>
            <div>
              <p className="font-bold text-[#1E3A5F]">تعديل حساب الزبون</p>
              <p className="text-[10px] text-[#5C6E64]">{customer.full_name}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="cs-edit-close" className="text-[#5C6E64] hover:bg-[#F3F6F4] p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current state indicator */}
        <div className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-xl p-2 text-center">
          <p className="text-[10px] text-[#5C6E64]">الرصيد الحالي</p>
          <p className={`text-lg font-black ${currentDebt > 0 ? "text-[#B91C1C]" : currentDebt < 0 ? "text-[#1E3A5F]" : "text-[#15803D]"}`} dir="ltr">
            {fmt(Math.abs(currentDebt))} د.ع
            <span className="text-[10px] mr-1">
              {currentDebt > 0 ? "(على الزبون)" : currentDebt < 0 ? "(علينا)" : "(مسدَّد)"}
            </span>
          </p>
        </div>

        {/* Mode selector — 2 clear buttons */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setMode("customer_owes")}
            data-testid="cs-edit-mode-owes"
            className={`rounded-xl p-3 border-2 transition-all text-right ${
              mode === "customer_owes"
                ? "border-[#B91C1C] bg-[#FEE2E2] text-[#B91C1C]"
                : "border-[#E4EBE6] bg-white text-[#5C6E64] hover:border-[#B91C1C]/50"
            }`}
          >
            <p className="text-lg mb-0.5">🔴</p>
            <p className="text-xs font-bold">على الزبون</p>
            <p className="text-[9px] opacity-80">(يطلبنا — دين علينا)</p>
          </button>
          <button
            onClick={() => setMode("we_owe")}
            data-testid="cs-edit-mode-we-owe"
            className={`rounded-xl p-3 border-2 transition-all text-right ${
              mode === "we_owe"
                ? "border-[#1E3A5F] bg-[#DBEAFE] text-[#1E3A5F]"
                : "border-[#E4EBE6] bg-white text-[#5C6E64] hover:border-[#1E3A5F]/50"
            }`}
          >
            <p className="text-lg mb-0.5">🔵</p>
            <p className="text-xs font-bold">علينا</p>
            <p className="text-[9px] opacity-80">(نطلبه — نحن ندين له)</p>
          </button>
        </div>

        {/* Value input */}
        <div>
          <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">
            المبلغ الجديد
          </label>
          <input
            type="number" inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="cs-edit-value"
            className="w-full px-3 py-3 rounded-xl border-2 border-[#F5A623] text-lg font-black text-center"
            dir="ltr"
            placeholder="0"
          />
          <p className="text-[10px] text-[#5C6E64] mt-1 text-center">
            {mode === "we_owe" ? "الرصيد سيصير بالسالب (يطلبنا)" : "الرصيد سيصير علينا"}
          </p>
        </div>

        {/* Reason (optional) */}
        <div>
          <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">
            سبب التعديل (اختياري)
          </label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="cs-edit-reason"
            placeholder="مثلاً: تسوية سابقة، خطأ إدخال..."
            className="w-full px-3 py-2 rounded-xl border-2 border-[#E4EBE6] text-sm"
          />
        </div>

        {/* Quick zero button */}
        {currentDebt !== 0 && (
          <button
            onClick={() => { setValue("0"); setMode("customer_owes"); }}
            data-testid="cs-edit-zero"
            className="w-full bg-[#DCFCE7] hover:bg-[#BBF7D0] text-[#15803D] rounded-xl py-2 text-xs font-bold"
          >
            ✓ صفّر الحساب (تسديد كامل)
          </button>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={saving}
            className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] rounded-xl py-2.5 font-bold text-sm">
            إلغاء
          </button>
          <button onClick={save} disabled={saving} data-testid="cs-edit-save"
            className="flex-1 bg-gradient-to-l from-[#F5A623] to-[#E85D45] hover:brightness-95 text-white rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Pencil className="w-4 h-4" /> حفظ التعديل</>}
          </button>
        </div>

        <p className="text-[9px] text-[#5C6E64] text-center italic">
          ⚡ التعديل ينعكس تلقائياً على كشف الحساب، الأرصدة، وتقارير المبيعات.
        </p>
      </div>
    </div>
  );
};

// ─── Main ───────────────────────────────────────────────────────
const CustomerStatement = ({ initialCustomer = null }) => {
  const [customer, setCustomer] = useState(initialCustomer);
  const [rangeMode, setRangeMode] = useState("all"); // "all" | "range"
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false); // balance edit modal

  const fetchLedger = async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const params = {};
      if (rangeMode === "range") {
        if (from) params.date_from = `${from}T00:00:00.000Z`;
        if (to) params.date_to = `${to}T23:59:59.999Z`;
      }
      const { data: res } = await axios.get(
        `${API}/admin/customers/${customer.id}/debt-ledger`,
        { params }
      );
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل كشف الحساب");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (customer) fetchLedger();
    else setData(null);
    // eslint-disable-next-line
  }, [customer, rangeMode, from, to]);

  const net = data?.summary?.net || 0;
  const isOwedByCustomer = net > 0;
  const isOwedToCustomer = net < 0;

  // ─── Print ─────────────────────────────────────────────────
  const buildPrintHtml = () => {
    if (!data || !customer) return "";
    const dateStr = new Date().toLocaleString("ar-IQ", { dateStyle: "medium", timeStyle: "short" });
    const rangeStr = rangeMode === "range"
      ? `من ${from || "—"} إلى ${to || "—"}`
      : "كشف كلّي (كل الحركات)";
    const rows = (data.events || []).map((ev, i) => {
      const isOrder = ev.type === "order";
      const isReceipt = ev.type === "receipt";
      const isPurchFromCust = ev.type === "purchase_from_customer";
      const delta = Number(ev.delta || 0);
      const balAfter = Number(ev.balance_after || 0);
      const deltaCls = delta > 0 ? "amt-red" : delta < 0 ? "amt-green" : "";
      const bg = isOrder
        ? (ev.source === "بيع المشرف" ? "row-purple" :
           ev.source === "بيع مباشر" ? "row-orange" : "row-blue")
        : isReceipt ? "row-green"
        : isPurchFromCust ? "row-cyan"
        : "row-navy";
      const meta = [];
      if (isOrder) {
        meta.push(ev.source_extra ? `${ev.source} — ${ev.source_extra}` : ev.source);
        meta.push(ev.confirmed ? "✓ مؤكدة" : "⏳ غير مؤكدة");
        if (ev.edited) meta.push("عُدّلت");
        if (ev.status && ev.status !== "confirmed") meta.push(STATUS_LABEL[ev.status] || ev.status);
        if (ev.items_count) meta.push(`${ev.items_count} صنف`);
        meta.push(`إجمالي ${fmt(ev.invoice_total)} د.ع`);
        if (ev.paid_at_creation > 0) meta.push(`مدفوع نقداً ${fmt(ev.paid_at_creation)}`);
      } else if (isReceipt) {
        if (ev.cash_amount > 0) meta.push(`نقداً ${fmt(ev.cash_amount)}`);
        if (ev.discount > 0) meta.push(`خصم ${fmt(ev.discount)}`);
        if (ev.notes) meta.push(ev.notes);
      } else if (isPurchFromCust) {
        meta.push("🔄 شراء من الزبون");
        if (ev.items_count) meta.push(`${ev.items_count} صنف`);
        meta.push(`إجمالي القائمة ${fmt(ev.invoice_total)} د.ع`);
        if (ev.paid_at_creation > 0) meta.push(`دفعنا له نقداً ${fmt(ev.paid_at_creation)}`);
        if (ev.edited) meta.push("عُدّلت");
      } else if (ev.type === "manual") {
        if (ev.actor_name) meta.push(`بواسطة ${ev.actor_name}`);
      }
      return `<tr class="${bg}">
        <td class="c" dir="ltr">${i + 1}</td>
        <td class="c" dir="ltr">${fmtDate(ev.date)}</td>
        <td>
          <div class="ev-title">${(ev.description || "").replace(/</g, "&lt;")}</div>
          ${meta.length ? `<div class="ev-meta">${meta.map((m) => `<span>${String(m).replace(/</g,"&lt;")}</span>`).join(" · ")}</div>` : ""}
        </td>
        <td class="c ${deltaCls}" dir="ltr">${delta > 0 ? "+" : ""}${fmt(delta)}</td>
        <td class="c" dir="ltr">${fmt(Math.abs(balAfter))}${balAfter < 0 ? " (له)" : ""}</td>
      </tr>`;
    }).join("");

    const netCls = isOwedByCustomer ? "grand-red" : isOwedToCustomer ? "grand-blue" : "grand-green";
    const netLabel = isOwedByCustomer
      ? "الرصيد الصافي: على الزبون (مدين لنا)"
      : isOwedToCustomer
      ? "الرصيد الصافي: يطلبنا (له عندنا)"
      : "الرصيد الصافي: مسدَّد بالكامل";
    const netVal = fmt(Math.abs(net));

    const supportHtml = SUPPORT_PHONES.map((p) => `${p.label}: <b dir="ltr">${p.local}</b>`).join(" · ");

    return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
      <title>كشف حساب — ${(customer.full_name || "").replace(/</g,"&lt;")}</title>
      <style>
        @page { size: A4; margin: 12mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: 'Tajawal','Tahoma','Segoe UI',Arial,sans-serif; padding: 20px; color: #111; direction: rtl; margin: 0; }
        .header {
          display: flex; justify-content: space-between; align-items: flex-start;
          padding-bottom: 14px; border-bottom: 3px solid #F5A623; margin-bottom: 14px;
        }
        .brand h1 { color: #E85D45; margin: 0; font-size: 24px; }
        .brand p { margin: 4px 0 0; color: #5C6E64; font-size: 12px; }
        .date-box { text-align: left; font-size: 11px; color: #5C6E64;
          background: #F8FAFC; border: 1px solid #E4EBE6; border-radius: 10px; padding: 8px 12px; }
        .date-box b { display: block; color: #1E3A5F; font-size: 13px; }
        .customer-card {
          background: linear-gradient(90deg,#1E3A5F,#3B82F6); color: #fff;
          border-radius: 14px; padding: 14px 16px; margin-bottom: 12px;
        }
        .customer-card h2 { margin: 0 0 4px; font-size: 20px; }
        .customer-card .sub { font-size: 12px; opacity: 0.9; }
        .filter-line { font-size: 11px; color: #5C6E64; margin-bottom: 8px; }
        .summary { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; margin-bottom: 12px; }
        .summary .card { border-radius: 10px; padding: 8px 10px; text-align: center; color: #fff; }
        .summary .c1 { background: #B91C1C; }
        .summary .c2 { background: #15803D; }
        .summary .c3 { background: #F5A623; }
        .summary .c4 { background: #1E3A5F; }
        .summary .lbl { font-size: 10px; opacity: 0.9; }
        .summary .val { font-size: 16px; font-weight: 900; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        thead th { background: #1E3A5F; color: #fff; padding: 7px 5px; text-align: right; }
        thead th.c { text-align: center; }
        tbody td { border: 1px solid #E4EBE6; padding: 6px; vertical-align: middle; }
        tbody td.c { text-align: center; }
        .row-blue td { background: #EFF6FF; }
        .row-orange td { background: #FFFBEB; }
        .row-purple td { background: #FAF5FF; }
        .row-green td { background: #F0FDF4; }
        .row-cyan td { background: #F0F9FF; }
        .row-navy td { background: #F0F9FF; }
        .ev-title { font-weight: 800; }
        .ev-meta { font-size: 10px; color: #5C6E64; margin-top: 2px; }
        .amt-red { color: #B91C1C; font-weight: 900; }
        .amt-green { color: #15803D; font-weight: 900; }
        .grand-row td { padding: 14px !important; font-size: 16px !important; font-weight: 900; text-align: center; }
        .grand-red td { background: #B91C1C !important; color: #fff !important; }
        .grand-blue td { background: #1E3A5F !important; color: #fff !important; }
        .grand-green td { background: #15803D !important; color: #fff !important; }
        .footer { margin-top: 24px; padding-top: 12px; border-top: 2px dashed #E4EBE6;
          text-align: center; font-size: 11px; color: #5C6E64; line-height: 1.7; }
        .footer .brand-line { color: #E85D45; font-weight: 900; font-size: 14px; }
      </style></head>
      <body>
        <div class="header">
          <div class="brand">
            <h1>جملة أون لاين</h1>
            <p>كشف حساب زبون · Jumlaty Online</p>
          </div>
          <div class="date-box">
            <b>تاريخ الطباعة</b>
            <span dir="ltr">${dateStr}</span>
          </div>
        </div>

        <div class="customer-card">
          <h2>${(customer.full_name || "").replace(/</g,"&lt;")}</h2>
          <div class="sub">
            ${customer.market_name ? `🏪 ${customer.market_name.replace(/</g,"&lt;")} · ` : ""}
            ${customer.customer_id ? `ايباد #${customer.customer_id} · ` : ""}
            <span dir="ltr">${(customer.phone||"").replace(/</g,"&lt;")}</span>
          </div>
        </div>

        <div class="filter-line">فترة الكشف: <b>${rangeStr}</b> · عدد الحركات: <b>${fmt(data.summary.events_count)}</b></div>

        <div class="summary">
          <div class="card c1"><div class="lbl">إجمالي الفواتير</div><div class="val" dir="ltr">${fmt(data.summary.total_orders_value)}</div></div>
          <div class="card c2"><div class="lbl">إجمالي القبض</div><div class="val" dir="ltr">${fmt(data.summary.total_received)}</div></div>
          <div class="card c3"><div class="lbl">مدفوع عند الفاتورة</div><div class="val" dir="ltr">${fmt(data.summary.total_paid_at_creation)}</div></div>
          <div class="card c4"><div class="lbl">تعديلات (+/-)</div><div class="val" dir="ltr">${fmt(data.summary.total_adjust_up)}/${fmt(data.summary.total_adjust_down)}</div></div>
        </div>

        <table>
          <thead>
            <tr>
              <th class="c" style="width:34px">#</th>
              <th class="c" style="width:130px">التاريخ</th>
              <th>الحركة</th>
              <th class="c" style="width:100px">القيمة</th>
              <th class="c" style="width:110px">الرصيد بعدها</th>
            </tr>
          </thead>
          <tbody>
            ${rows || `<tr><td colspan="5" class="c" style="padding:20px;color:#5C6E64">لا توجد حركات في هذه الفترة</td></tr>`}
            <tr class="grand-row ${netCls}">
              <td colspan="4">${netLabel}</td>
              <td dir="ltr">${netVal} د.ع</td>
            </tr>
          </tbody>
        </table>

        <div class="footer">
          <div class="brand-line">جملة أون لاين — Jumlaty Online</div>
          <div>${supportHtml}</div>
          <div>الموقع: jumlaty.online</div>
        </div>
      </body></html>`;
  };

  const handlePrint = () => {
    if (!data || !customer) return;
    printHTML(buildPrintHtml(), { title: `كشف ${customer.full_name}` });
  };

  const handleWhatsApp = () => {
    if (!data || !customer) return;
    const dateStr = new Date().toLocaleDateString("ar-IQ");
    const lines = [];
    lines.push("*📋 كشف حساب زبون — جملة أون لاين*");
    lines.push(`الزبون: *${customer.full_name}*`);
    if (customer.market_name) lines.push(`الماركت: ${customer.market_name}`);
    lines.push(`الهاتف: ${customer.phone}`);
    lines.push(`تاريخ الكشف: ${dateStr}`);
    lines.push(`الفترة: ${rangeMode === "range" ? `${from || "—"} → ${to || "—"}` : "كلّي"}`);
    lines.push("─".repeat(24));
    (data.events || []).forEach((ev, i) => {
      const delta = Number(ev.delta || 0);
      const sign = delta > 0 ? "➕" : delta < 0 ? "➖" : "•";
      const bal = Number(ev.balance_after || 0);
      lines.push(`${i + 1}. ${sign} ${ev.description}`);
      lines.push(`   📅 ${fmtDate(ev.date)}`);
      lines.push(`   قيمة: ${delta > 0 ? "+" : ""}${fmt(delta)}  ·  الرصيد: ${fmt(Math.abs(bal))}${bal < 0 ? " (له)" : ""}`);
    });
    lines.push("─".repeat(24));
    const netLine = isOwedByCustomer
      ? `*💵 الرصيد الصافي: ${fmt(Math.abs(net))} د.ع (على الزبون)*`
      : isOwedToCustomer
      ? `*💰 الرصيد الصافي: ${fmt(Math.abs(net))} د.ع (يطلبنا)*`
      : `*✅ الرصيد مسدّد بالكامل*`;
    lines.push(netLine);
    lines.push("");
    lines.push("*جملة أون لاين — Jumlaty Online*");
    SUPPORT_PHONES.forEach((p) => lines.push(`${p.label}: ${p.local}`));
    lines.push("🌐 jumlaty.online");
    const url = `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="customer-statement">
      <CustomerPicker
        selected={customer}
        onSelect={setCustomer}
        onClear={() => { setCustomer(null); setData(null); }}
      />

      {customer && (
        <>
          {/* Balance Edit button — inline, right below the picker so it's
              obvious. Opens a modal to increase/decrease/set the balance. */}
          <button
            onClick={() => setEditOpen(true)}
            data-testid="cs-edit-balance-btn"
            className="w-full bg-gradient-to-l from-[#F5A623] to-[#dc9320] hover:from-[#dc9320] hover:to-[#c48420] text-white font-bold rounded-2xl py-2.5 px-4 flex items-center justify-center gap-2 shadow-lg transition-all"
          >
            <Pencil className="w-4 h-4" />
            تعديل حساب الزبون (رصيد له / علينا)
          </button>
        </>
      )}

      {editOpen && customer && data && (
        <BalanceEditModal
          customer={customer}
          currentDebt={data.customer.current_debt_stored || 0}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); fetchLedger(); }}
        />
      )}

      {customer && (
        <>
          {/* Range mode toggle */}
          <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3">
            <p className="text-xs font-bold text-[#1E3A5F] mb-2">فترة الكشف</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                onClick={() => setRangeMode("all")}
                data-testid="cs-mode-all"
                className={`rounded-xl py-2 text-sm font-bold transition-colors ${
                  rangeMode === "all" ? "bg-[#1E3A5F] text-white" : "bg-[#F3F6F4] text-[#5C6E64]"
                }`}
              >
                🗂 كشف كلّي (كل الحركات)
              </button>
              <button
                onClick={() => setRangeMode("range")}
                data-testid="cs-mode-range"
                className={`rounded-xl py-2 text-sm font-bold transition-colors ${
                  rangeMode === "range" ? "bg-[#F5A623] text-white" : "bg-[#F3F6F4] text-[#5C6E64]"
                }`}
              >
                📆 من تاريخ إلى تاريخ
              </button>
            </div>
            {rangeMode === "range" && (
              <div className="grid grid-cols-2 gap-2" data-testid="cs-range-inputs">
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                    data-testid="cs-from"
                    className="w-full pr-10 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 bg-white text-sm" />
                </div>
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5A623]" />
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                    data-testid="cs-to"
                    className="w-full pr-10 pl-2 py-2 rounded-xl border-2 border-[#F5A623]/50 bg-white text-sm" />
                </div>
              </div>
            )}
          </div>

          {/* Export toolbar */}
          {data && data.events && data.events.length > 0 && (
            <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 flex flex-wrap gap-2 items-center justify-between">
              <p className="text-xs font-bold text-[#5C6E64]">
                📤 تصدير كشف حساب <b className="text-[#1E3A5F]">{customer.full_name}</b>:
              </p>
              <div className="flex flex-wrap gap-2">
                <button onClick={handlePrint} data-testid="cs-print-btn"
                  className="bg-[#1E3A5F] hover:bg-[#152a45] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
                  <Printer className="w-3.5 h-3.5" /> طباعة
                </button>
                <button onClick={handlePrint} data-testid="cs-pdf-btn"
                  className="bg-[#B91C1C] hover:bg-[#991B1B] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> PDF
                </button>
                <button onClick={handleWhatsApp} data-testid="cs-whatsapp-btn"
                  className="bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5">
                  <Share2 className="w-3.5 h-3.5" /> واتساب
                </button>
              </div>
            </div>
          )}

          {/* Summary cards */}
          {data && (
            <>
              {data.customer && data.customer.can_buy_from && (
                <div className="bg-gradient-to-l from-[#0EA5E9] to-[#38BDF8] text-white rounded-2xl p-3 flex items-center gap-2">
                  <span className="text-2xl">🔄</span>
                  <div className="flex-1">
                    <p className="font-bold text-sm">حساب متبادل مفعّل</p>
                    <p className="text-[11px] opacity-90">
                      هذا الزبون مفعّل كمُجهّز أيضاً — الكشف يعرض مبيعاتنا له <b>ومشترياتنا منه</b> بحساب موحّد.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SummaryCard label="إجمالي الفواتير (بيع له)" val={fmt(data.summary.total_orders_value)}
                  cls="bg-gradient-to-l from-[#B91C1C] to-[#DC2626]" />
                <SummaryCard label="إجمالي القبض" val={fmt(data.summary.total_received)}
                  cls="bg-gradient-to-l from-[#15803D] to-[#22C55E]" />
                {(data.summary.purchases_from_customer_count || 0) > 0 ? (
                  <SummaryCard label="شراء منّه (قوائم)" val={fmt(data.summary.total_purchases_from_customer)}
                    cls="bg-gradient-to-l from-[#0EA5E9] to-[#38BDF8]" />
                ) : (
                  <SummaryCard label="مدفوع عند الفاتورة" val={fmt(data.summary.total_paid_at_creation)}
                    cls="bg-gradient-to-l from-[#F5A623] to-[#dc9320]" />
                )}
                <SummaryCard label="عدد الحركات" val={fmt(data.summary.events_count)}
                  cls="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6]" />
              </div>
            </>
          )}

          {/* Timeline */}
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>
          ) : !data || !data.events || data.events.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-[#E4EBE6] rounded-2xl p-8 text-center text-[#5C6E64]">
              <ChevronLeft className="w-10 h-10 mx-auto opacity-30 mb-2" />
              <p className="font-bold">لا توجد حركات في هذه الفترة</p>
              <p className="text-xs mt-1">جرّب فترة أوسع أو اختر &quot;كشف كلّي&quot;</p>
            </div>
          ) : (
            <div data-testid="cs-timeline">
              {data.events.map((ev, i) => (
                <EventCard key={i} ev={ev} idx={i} isLast={i === data.events.length - 1} />
              ))}
            </div>
          )}

          {/* Final net balance block — big, colored */}
          {data && data.events && data.events.length > 0 && (
            <div
              data-testid="cs-net-block"
              className={`rounded-3xl p-5 text-white shadow-lg ${
                isOwedByCustomer
                  ? "bg-gradient-to-l from-[#B91C1C] to-[#DC2626]"
                  : isOwedToCustomer
                  ? "bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6]"
                  : "bg-gradient-to-l from-[#15803D] to-[#22C55E]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full w-14 h-14 flex items-center justify-center shrink-0">
                  {isOwedByCustomer
                    ? <ArrowUpRight className="w-7 h-7" />
                    : isOwedToCustomer
                    ? <ArrowDownLeft className="w-7 h-7" />
                    : <span className="text-3xl">✓</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs opacity-90">الرصيد الصافي النهائي</p>
                  <p className="text-3xl font-black" dir="ltr">
                    {fmt(Math.abs(net))} د.ع
                  </p>
                  <p className="text-sm font-bold mt-1">
                    {isOwedByCustomer
                      ? "🔴 على الزبون (مدين لنا بهذا المبلغ)"
                      : isOwedToCustomer
                      ? "🔵 يطلبنا (لهُ عندنا هذا المبلغ)"
                      : "🟢 الحساب مسدَّد بالكامل"}
                  </p>
                </div>
              </div>
              {Math.abs(net - (data.customer.current_debt_stored || 0)) > 1 && (
                <div className="mt-3 bg-white/15 rounded-xl p-3 text-[11px] space-y-2">
                  <p>
                    ⚠ الرصيد المحسوب من الحركات <b dir="ltr">{fmt(net)}</b> يختلف عن الرصيد المخزّن <b dir="ltr">{fmt(data.customer.current_debt_stored)}</b>.
                  </p>
                  <button
                    data-testid="cs-fix-drift-btn"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/customer-debt-audit/${data.customer.id}/fix`);
                        toast.success("✓ تم إصلاح رصيد الزبون — سيتطابق بالأرصدة الآن");
                        // Refresh statement to show fixed value
                        setTimeout(() => fetchLedger(), 300);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "فشل الإصلاح");
                      }
                    }}
                    className="w-full bg-white text-[#B91C1C] hover:bg-white/90 font-black rounded-xl py-2 flex items-center justify-center gap-1"
                  >
                    🔧 اضغط هنا لإصلاح رصيد هذا الزبون فوراً
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!customer && (
        <div className="bg-white border-2 border-dashed border-[#E4EBE6] rounded-2xl p-10 text-center text-[#5C6E64]">
          <User className="w-14 h-14 mx-auto opacity-30 mb-2" />
          <p className="font-bold text-lg">اختر زبوناً لعرض كشف حسابه الكامل</p>
          <p className="text-xs mt-2">
            راح تظهر كل حركاته: فواتير (بيع مباشر / بيع مشرف / تطبيق) · سندات قبض · تعديلات يدوية — مرتبة زمنياً مع رصيد جاري.
          </p>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, val, cls }) => (
  <div className={`${cls} text-white rounded-2xl p-3 text-center`}>
    <p className="text-[10px] opacity-90">{label}</p>
    <p className="text-lg font-black mt-0.5" dir="ltr">{val}</p>
  </div>
);

export default CustomerStatement;
